import { useEffect, useRef } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { setAuthState } from '@/lib/authStatus'
import { setAuthTokenGetter } from '@/lib/authToken'
import { reconcileLocalStoreForUser } from '@/lib/userScopeStorage'
import { AUTH_EXPIRED_EVENT, FORCE_LOGOUT_EVENT, type AuthExpiredEventDetail, type ForceLogoutEventDetail } from '@/lib/authEvents'
import { auth0Config } from '@/config/auth0'
import { markForcedLogout, wasForcedLogoutRecently } from '@/lib/recoveryRefreshLoop'

export function AuthController(): null {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, loginWithRedirect, logout } = useAuth0()
  const redirectInFlightRef = useRef(false)
  const lastRedirectAtRef = useRef(0)
  const forceLogoutInFlightRef = useRef(false)

  useEffect(() => {
    setAuthState({
      isAuthenticated,
      isLoading,
      userSub: user?.sub ?? null,
      userName: user?.name ?? null,
      userEmail: user?.email ?? null,
    })

    if (isAuthenticated) {
      setAuthTokenGetter(async () => {
        try {
          return await getAccessTokenSilently()
        } catch {
          return null
        }
      })
      // Drop the previous account's cached recordings when a different user
      // signs in on this browser (IndexedDB survives logout).
      if (user?.sub) {
        void reconcileLocalStoreForUser(user.sub)
      }
    } else {
      setAuthTokenGetter(null)
    }
  }, [isAuthenticated, isLoading, user, getAccessTokenSilently])

  useEffect(() => {
    const handleForceLogoutEvent = (event: Event): void => {
      const custom = event as CustomEvent<ForceLogoutEventDetail>
      const now = Date.now()
      if (forceLogoutInFlightRef.current || wasForcedLogoutRecently(now)) {
        return
      }
      forceLogoutInFlightRef.current = true
      markForcedLogout(now)
      const online = navigator.onLine

      logout({
        logoutParams: {
          returnTo: auth0Config.logoutUri,
        },
        openUrl: online ? undefined : false,
      })
        .then(() => {
          if (!online) {
            window.location.assign(auth0Config.logoutUri)
          }
        })
        .catch((error) => {
          console.warn('Forced logout failed', { detail: custom.detail, error })
          window.location.assign(auth0Config.logoutUri)
        })
        .finally(() => {
          forceLogoutInFlightRef.current = false
        })
    }

    window.addEventListener(FORCE_LOGOUT_EVENT, handleForceLogoutEvent as EventListener)
    return () => {
      window.removeEventListener(FORCE_LOGOUT_EVENT, handleForceLogoutEvent as EventListener)
    }
  }, [logout])

  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      return
    }

    const shouldSkipRedirect = (): boolean => {
      const path = window.location.pathname
      return path.startsWith('/auth/callback') || path.startsWith('/logout-complete')
    }

    const redirectToLogin = async (detail?: AuthExpiredEventDetail): Promise<void> => {
      if (!navigator.onLine || shouldSkipRedirect()) {
        return
      }

      const now = Date.now()
      if (redirectInFlightRef.current || now - lastRedirectAtRef.current < 5_000) {
        return
      }

      redirectInFlightRef.current = true
      lastRedirectAtRef.current = now
      try {
        const runtimeRedirectUri = `${window.location.origin}/auth/callback`
        await loginWithRedirect({
          appState: { returnTo: window.location.pathname },
          authorizationParams: {
            redirect_uri: runtimeRedirectUri,
          },
        })
      } catch (error) {
        console.warn('Auth redirect failed', { detail, error })
        redirectInFlightRef.current = false
      }
    }

    const verifyTokenOnResume = async (): Promise<void> => {
      if (!navigator.onLine || shouldSkipRedirect()) {
        return
      }
      try {
        await getAccessTokenSilently()
        setAuthState({ tokenStatus: 'ready' })
      } catch (error) {
        console.warn('Token unavailable on resume/focus', error)
        setAuthState({ tokenStatus: 'expired' })
        await redirectToLogin({ context: 'resume/focus', reason: 'token_unavailable' })
      }
    }

    const handleAuthExpiredEvent = (event: Event): void => {
      const custom = event as CustomEvent<AuthExpiredEventDetail>
      void redirectToLogin(custom.detail)
    }

    const handleFocus = (): void => {
      void verifyTokenOnResume()
    }

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        void verifyTokenOnResume()
      }
    }

    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpiredEvent as EventListener)
    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Run once on mount to catch already-expired sessions.
    void verifyTokenOnResume()

    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpiredEvent as EventListener)
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect])

  return null
}
